import { useEffect, useRef, useState, useCallback } from 'react';

import * as Comlink from 'comlink';
import { useShallow } from 'zustand/react/shallow';

import { useVideoBackground } from '.';

import { delay, getDimensions } from '@src/utils';
import { createCanvasElement, createVideoElement } from '@src/helpers';
import { createTimerWorker } from '@src/workers/worker-loader';
import type { VirtualBackgroundWorkerType } from '@src/workers/virtual-background.worker';
import { virtualBackgroundService, VideoStreamService } from '@src/services';

import { TBackgroundEffect } from '@src/types';
import { useVideoStreamStore, useProducersStore, useVirtualBackgroundStore } from '@src/stores';

interface IProps {
  isReadyToProcess: boolean;
  instanceId: string;
  isMobileDevice?: boolean;
}

const useVirtualBackgroundHandler = ({ isReadyToProcess, instanceId, isMobileDevice = false }: IProps) => {
  const [isWorkerReady, setIsWorkerReady] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [errorState, setErrorState] = useState<string | null>(null);

  const vbCanvasRef = useRef<HTMLCanvasElement | null>(null);
  // CPU OPTIMIZATION: Video element created lazily when originalStream exists
  const streamVideoRef = useRef<HTMLVideoElement | null>(null);
  const vbVideoRef = useRef<HTMLVideoElement | null>(null);

  const workerApiRef = useRef<Comlink.Remote<VirtualBackgroundWorkerType> | null>(null);

  const timeoutRef = useRef<number | null>(null);
  const animationFrameRef = useRef<number>(0);
  const isProcessingActive = useRef(false);
  const isCanvasTransferred = useRef(false);
  const lastEffectRef = useRef<TBackgroundEffect | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const orientationListenerRef = useRef<(() => void) | null>(null);
  const hasAcquiredWorker = useRef(false);

  // BACKGROUND TAB FIX: Timer worker refs for consistent frame rate
  const timerWorkerRef = useRef<Worker | null>(null);
  const pendingFrameRef = useRef(false);

  const currentVideoSizeRef = useRef({ width: 0, height: 0 });
  const isReadyToProcessRef = useRef(isReadyToProcess);
  const isInitializedRef = useRef(isInitialized);
  const isStreamCreated = useRef(false); // Track if canvas stream has been created
  const isInitializingRef = useRef(false); // MUTEX: Prevent double initialization

  const { originalStream } = useVideoStreamStore(
    useShallow((state) => ({
      originalStream: state.originalStream,
    }))
  );

  const { config, appliedEffect, selectedEffect, detectAndApplyPerformanceSettings, deviceCapabilities } =
    useVirtualBackgroundStore(
      useShallow((state) => ({
        config: state.config,
        appliedEffect: state.appliedEffect,
        selectedEffect: state.selectedEffect,
        detectAndApplyPerformanceSettings: state.detectAndApplyPerformanceSettings,
        deviceCapabilities: state.deviceCapabilities,
      }))
    );

  // Only detect performance settings when we need them
  // This avoids running GPU detection on every mount
  useEffect(() => {
    const hasEffect = appliedEffect.type !== 'none';
    if (hasEffect && !deviceCapabilities) {
      detectAndApplyPerformanceSettings();
    }
  }, [detectAndApplyPerformanceSettings, appliedEffect.type, deviceCapabilities]);

  const isVideoPlayingRef = useRef(false);
  const hiddenVideoStreamRef = useRef<MediaStream | null>(null);

  // Assign original stream to hidden video element for processing
  // AND reset state if stream is removed
  // Video element is created LAZILY only when needed
  useEffect(() => {
    // Check EITHER applied OR selected effect to support preview
    const hasActiveEffect =
      (appliedEffect.type !== 'none' && appliedEffect.value) ||
      (selectedEffect.type !== 'none' && selectedEffect.value);

    // Case 1: We have originalStream AND an active effect - need video element
    if (originalStream && hasActiveEffect) {
      // LAZY CREATION: Only create video element when we actually need it
      if (!streamVideoRef.current) {
        streamVideoRef.current = createVideoElement({});
      }

      const videoElement = streamVideoRef.current;
      const videoTrack = originalStream.getVideoTracks()[0];

      if (videoTrack && videoTrack.readyState === 'live') {
        const clonedTrack = videoTrack?.clone();
        clonedTrack?.applyConstraints({
          width: { ideal: 1280 },
          height: { ideal: 720 },
        });
        // Create a stream for the hidden video (reuses original track, DO NOT stop it)
        const hiddenStream = new MediaStream([clonedTrack]);
        VideoStreamService.assignStream(videoElement, hiddenStream);
        hiddenVideoStreamRef.current = hiddenStream;

        // Play the video for processing
        if (!isVideoPlayingRef.current) {
          videoElement
            .play()
            .then(() => {
              isVideoPlayingRef.current = true;
            })
            .catch((e) => console.warn('[VB] Hidden video play error:', e));
        }
      }
    }
    // Case 2: No active effect or no stream - cleanup
    else {
      const videoElement = streamVideoRef.current;

      if (videoElement) {
        // Pause if playing
        if (isVideoPlayingRef.current) {
          videoElement.pause();
          isVideoPlayingRef.current = false;
        }

        // Clear stream assignment (DON'T stop tracks - they belong to originalStream!)
        VideoStreamService.assignStream(videoElement, null);

        !originalStream && VideoStreamService.stopStream(hiddenVideoStreamRef.current);
        hiddenVideoStreamRef.current = null;
      }

      // Reset internal flags when no stream
      if (!originalStream) {
        isStreamCreated.current = false;
        streamRef.current = null;
        useVideoStreamStore.getState().setModifiedStream(null);
      }
    }
  }, [originalStream, appliedEffect.type, appliedEffect.value, selectedEffect.type, selectedEffect.value]);

  // Keep refs in sync with props/state for the async callbacks
  useEffect(() => {
    isReadyToProcessRef.current = isReadyToProcess;
  }, [isReadyToProcess]);

  useEffect(() => {
    isInitializedRef.current = isInitialized;
  }, [isInitialized]);

  // Canvas creation is now LAZY
  // Only create canvas when we have an active effect
  useEffect(() => {
    const hasActiveEffect = appliedEffect.type !== 'none' || selectedEffect.type !== 'none';

    // Only create canvas if we need it and don't have one
    if (hasActiveEffect && vbCanvasRef.current === null) {
      const video = streamVideoRef.current;
      const initialDimensions =
        video && video.videoWidth > 0
          ? { width: video.videoWidth, height: video.videoHeight }
          : getDimensions(isMobileDevice);

      vbCanvasRef.current = createCanvasElement(initialDimensions);

      // CRITICAL: Canvas must be in DOM for captureStream() to work
      vbCanvasRef.current.style.display = 'none';
      document.body.appendChild(vbCanvasRef.current);
      console.log('[VB] Canvas created lazily for active effect');
    }
  }, [isMobileDevice, appliedEffect.type, selectedEffect.type]);

  // Handle orientation changes
  useEffect(() => {
    // CPU OPTIMIZATION: Skip if no effect active or not mobile
    const hasActiveEffect = appliedEffect.type !== 'none' || selectedEffect.type !== 'none';
    if (!isMobileDevice || !hasActiveEffect) return;

    const handleOrientationChange = () => {
      if (vbCanvasRef.current) {
        const { width, height } = getDimensions(true);

        if (workerApiRef.current) {
          workerApiRef.current.setCanvasSize(width, height).catch(console.error);
        }
      }
    };

    const mediaQuery = window.matchMedia('(orientation: portrait)');
    mediaQuery.addEventListener('change', handleOrientationChange);
    orientationListenerRef.current = () => {
      mediaQuery.removeEventListener('change', handleOrientationChange);
    };

    return () => {
      if (orientationListenerRef.current) {
        orientationListenerRef.current();
      }
    };
  }, [isWorkerReady, isMobileDevice, appliedEffect.type, selectedEffect.type]);

  // Video background effect (for animations)
  // Priority: Split into active and preview to allow independent animations
  const activeAnimationUrl = appliedEffect.type === 'animation' ? appliedEffect.value : '';

  // Only load preview if it's different from active active
  // (optimization: invalid url or match logic handled inside hook or worker fallback)
  const isDifferentPreview = selectedEffect.type === 'animation' && selectedEffect.value !== appliedEffect.value;
  const previewAnimationUrl = isDifferentPreview ? selectedEffect.value : '';

  // 1. Handle Active Animation
  useVideoBackground(activeAnimationUrl, workerApiRef.current, isMobileDevice, config.segmentationFPS, 'active');

  // 2. Handle Preview Animation (Dedicated loop)
  useVideoBackground(previewAnimationUrl, workerApiRef.current, isMobileDevice, config.segmentationFPS, 'preview');

  // Helper to fully terminate timer worker
  const terminateTimerWorker = useCallback(() => {
    if (timerWorkerRef.current) {
      timerWorkerRef.current.postMessage({ type: 'stop' });
      timerWorkerRef.current.terminate();
      timerWorkerRef.current = null;
    }
    pendingFrameRef.current = false;
  }, []);

  // Helper to stop stream tracks properly
  const stopStreamRef = useCallback(() => {
    if (streamRef.current) {
      VideoStreamService.stopStream(streamRef.current);
      streamRef.current = null;
    }
    isStreamCreated.current = false;
    useVideoStreamStore.getState().setModifiedStream(null);
  }, []);

  // Full cleanup for unmount
  const fullCleanup = useCallback(() => {
    isProcessingActive.current = false;
    cancelAnimationFrame(animationFrameRef.current);

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Explicit cleanup for video ref
    if (vbVideoRef.current) {
      vbVideoRef.current.srcObject = null;
    }

    // Remove canvas from DOM if it exists
    if (vbCanvasRef.current && vbCanvasRef.current.parentNode) {
      document.body.removeChild(vbCanvasRef.current);
    }

    // CPU FIX: Terminate timer worker during full cleanup
    terminateTimerWorker();

    // Stop stream and clean up tracks
    stopStreamRef();

    // Release worker reference (Singleton stays alive but knows we left)
    if (hasAcquiredWorker.current) {
      virtualBackgroundService.releaseWorker();
      hasAcquiredWorker.current = false;
    }

    // We do NOT terminate the worker or API here, just our local use
    workerApiRef.current = null;
    setIsWorkerReady(false);
    setIsInitialized(false);
  }, [terminateTimerWorker, stopStreamRef]);

  const handleResetStates = useCallback(() => {
    isProcessingActive.current = false;
    cancelAnimationFrame(animationFrameRef.current);
    animationFrameRef.current = 0;

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    // Terminate timer worker during reset, not just stop
    terminateTimerWorker();

    stopStreamRef();

    // FULL CLEANUP: Release worker and destroy canvas when resetting (e.g. camera off)
    // This fixes memory leaks by ensuring we don't hold onto the worker indefinitely
    if (vbCanvasRef.current && vbCanvasRef.current.parentNode) {
      document.body.removeChild(vbCanvasRef.current);
      vbCanvasRef.current = null;
      isCanvasTransferred.current = false;
    }

    if (hasAcquiredWorker.current) {
      virtualBackgroundService.releaseWorker();
      hasAcquiredWorker.current = false;
    }

    // Clear cached effect to force re-init when next effect is applied
    lastEffectRef.current = null;
    workerApiRef.current = null;
    setIsWorkerReady(false);

    setIsInitialized(false);
    setErrorState(null);
  }, [terminateTimerWorker, stopStreamRef]);

  // Process frames loop
  const processFrame = useCallback(async () => {
    if (!isReadyToProcessRef.current) return;

    // Check if processing was stopped while this frame was pending
    // This prevents stale closures from creating streams after effect is cleared
    if (!isProcessingActive.current) {
      return;
    }

    // Read current effect from store, not from closure
    // The closure might have a stale backgroundEffect if timer triggered before re-render
    const { appliedEffect, selectedEffect } = useVirtualBackgroundStore.getState();
    const hasActiveEffect = appliedEffect.type !== 'none' || selectedEffect.type !== 'none';
    if (!hasActiveEffect) {
      return;
    }

    // Timer worker handles frame rate - no need for manual throttling here

    const video = streamVideoRef.current;
    const workerApi = workerApiRef.current;

    if (!video || !workerApi || video.videoWidth === 0 || video.videoHeight === 0) {
      // Video not ready - timer worker will call us again
      return;
    }

    try {
      if (
        video.videoWidth !== currentVideoSizeRef.current.width ||
        video.videoHeight !== currentVideoSizeRef.current.height
      ) {
        // Use full video resolution
        // This avoids cropping/aspect-ratio issues reported by user
        // and simplifies the logic. Heavy models might slow down on 4K,
        // but correctness is priority.
        const width = video.videoWidth;
        const height = video.videoHeight;

        currentVideoSizeRef.current = { width, height };

        // Update worker size (this is the output resolution Mediasoup sees)
        await workerApi.setCanvasSize(width, height).catch(console.error);

        // Update DOM canvas size target (Only if not yet transferred, though logic ensures it is transferred by now usually)
        if (vbCanvasRef.current && !isCanvasTransferred.current) {
          vbCanvasRef.current.width = width;
          vbCanvasRef.current.height = height;
        }
      }

      // Ensure video is ready before capturing
      if (video.readyState < 2) {
        // Video not ready yet (no current data)
        return;
      }

      // CPU OPTIMIZATION: Use VideoFrame API for zero-copy when available (Chrome 94+)
      // VideoFrame is transferred directly to worker, avoiding main-thread pixel copy
      // The worker-side conversion happens off the main thread, improving responsiveness
      // Note: VideoFrame is a newer API without built-in TypeScript types
      let frame: ImageBitmap | VideoFrame | null = null;
      try {
        // Check if VideoFrame API is available (Chrome 94+, Firefox 106+)
        const VideoFrameConstructor = (globalThis as any).VideoFrame;
        if (VideoFrameConstructor) {
          // Modern browsers: Send VideoFrame directly to worker (zero-copy transfer)
          frame = new VideoFrameConstructor(video, { timestamp: performance.now() * 1000 });
          // Transfer VideoFrame directly - worker handles conversion
          await workerApi.processFrame(Comlink.transfer(frame as any, [frame as any]));
          frame = null; // Ownership transferred to worker
        } else {
          // Legacy fallback: Create ImageBitmap on main thread
          frame = await createImageBitmap(video);
          await workerApi.processFrame(Comlink.transfer(frame, [frame]));
          frame = null; // Ownership transferred to worker
        }
      } finally {
        // Only close if we still own it (wasn't transferred)
        if (frame) {
          frame.close();
        }
      }

      // 3. HANDLE STREAM CREATION
      if (vbCanvasRef.current) {
        // Re-verify flags right before stream logic
        const latestEffect = useVirtualBackgroundStore.getState().appliedEffect;
        const latestSelectedEffect = useVirtualBackgroundStore.getState().selectedEffect;
        const hasActiveEffect = latestEffect.type !== 'none' || latestSelectedEffect.type !== 'none';

        if (hasActiveEffect && !isStreamCreated.current && streamRef.current === null) {
          // One final check to ensure we didn't just stop
          if (!isProcessingActive.current) return;

          const isBackgroundReady = await workerApi.isBackgroundReady().catch(() => true);
          if (!isBackgroundReady) return;

          const { segmentationFPS } = useVirtualBackgroundStore.getState().config;
          const targetFPS = segmentationFPS || (isMobileDevice ? 15 : 30);
          const stream = vbCanvasRef.current.captureStream(targetFPS);
          streamRef.current = stream;
          isStreamCreated.current = true;

          console.log('[VB] Stream created - background is ready');
          VideoStreamService.assignStream(vbVideoRef.current, stream);
          useVideoStreamStore.getState().setModifiedStream(stream);
        } else if (!hasActiveEffect && isStreamCreated.current) {
          useVideoStreamStore.getState().setModifiedStream(null);
          streamRef.current = null;
          isStreamCreated.current = false;
        }
      }

      if (!isInitializedRef.current) {
        setIsInitialized(true);
      }
    } catch (error: any) {
      console.error(`[${instanceId}] Frame processing error:`, error);
      if (
        error.name === 'InvalidStateError' ||
        error.message?.includes('InvalidStateError') ||
        error.message?.includes('CONTEXT_LOST') ||
        error.message?.includes('destroyed')
      ) {
        console.warn(`[${instanceId}] Critical error detected, resetting state for recovery`);
        setErrorState('processing_failure');
        handleResetStates();
        return;
      }
    }
  }, [handleResetStates, instanceId, isMobileDevice]);

  // Helper to ensure timer worker exists
  const ensureTimerWorker = useCallback(() => {
    if (!timerWorkerRef.current) {
      timerWorkerRef.current = createTimerWorker();
      timerWorkerRef.current.onmessage = (e) => {
        if (e.data.type === 'tick' && !pendingFrameRef.current && isProcessingActive.current) {
          pendingFrameRef.current = true;
          processFrame().finally(() => {
            pendingFrameRef.current = false;
          });
        }
      };
    }
    return timerWorkerRef.current;
  }, [processFrame]);

  // Handle automatic processing start (with lazy worker creation)
  useEffect(() => {
    // Check if EITHER applied OR selected effect is active
    // This allows preview to work even if live effect is 'none'
    const { appliedEffect, selectedEffect } = useVirtualBackgroundStore.getState();
    const hasActiveEffect = appliedEffect.type !== 'none' || selectedEffect.type !== 'none';

    const shouldRun = isReadyToProcess && isWorkerReady && hasActiveEffect;

    if (shouldRun) {
      // LAZY CREATION: Only create timer worker when first effect is applied
      const timerWorker = ensureTimerWorker();
      const targetFPS = config.segmentationFPS || 20;

      // Always update timer interval (Worker handles restart)
      timerWorker.postMessage({
        type: 'start',
        interval: 1000 / targetFPS,
      });

      if (!isProcessingActive.current) {
        isProcessingActive.current = true;
        console.log('[VB] Started processing with timer worker at', targetFPS, 'FPS');
      }
    } else {
      // Stop timer worker
      if (isProcessingActive.current) {
        console.log('[VB] Stopping processing loop (Effect is None or disabled)');
        // Terminate timer worker completely to stop CPU usage
        terminateTimerWorker();

        cancelAnimationFrame(animationFrameRef.current);
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }
        isProcessingActive.current = false;

        // If effect is none, clear modified stream to use original
        // Using check against type here too for consistency
        // If effect is none, perform full reset to free all resources
        if (appliedEffect.type === 'none' && selectedEffect.type === 'none') {
          handleResetStates();
        } else if (appliedEffect.type === 'none' && isStreamCreated.current) {
          stopStreamRef();
        }
      }
    }
  }, [
    isReadyToProcess,
    isWorkerReady,
    appliedEffect.value,
    appliedEffect.type,
    ensureTimerWorker,
    config.segmentationFPS,
    terminateTimerWorker,
    stopStreamRef,
    handleResetStates,
  ]);

  // Initialize worker connection and canvas
  const initWorkerAndCanvas = useCallback(async () => {
    // MUTEX: Prevent double initialization from rapid calls
    if (isInitializingRef.current) {
      console.log('[VB] Init already in progress, skipping');
      return false;
    }
    if (isWorkerReady && isCanvasTransferred.current) return true;

    isInitializingRef.current = true;

    // Check if we already have a canvas, if not create it (can happen if we cleaned up)
    if (!vbCanvasRef.current) {
      const video = streamVideoRef.current;
      const initialDimensions =
        video && video.videoWidth > 0
          ? { width: video.videoWidth, height: video.videoHeight }
          : getDimensions(isMobileDevice);
      vbCanvasRef.current = createCanvasElement(initialDimensions);
      vbCanvasRef.current.style.display = 'none';
      document.body.appendChild(vbCanvasRef.current);
    }

    if (!vbCanvasRef.current) return false;

    try {
      // Only acquire worker if we don't not have it yet
      // This prevents multiple calls from incrementing RefCount multiple times
      if (!hasAcquiredWorker.current) {
        // SET FLAG SYNCHRONOUSLY to block race conditions
        hasAcquiredWorker.current = true;
        try {
          const api = await virtualBackgroundService.getWorkerApi();
          workerApiRef.current = api;
        } catch (error) {
          hasAcquiredWorker.current = false; // Reset on failure
          throw error;
        }
      }

      const api = workerApiRef.current;
      if (!api) return false; // Should not happen

      // PHASE 2 OPTIMIZATION: Transfer Canvas Control to Worker
      // This allows worker to draw directly to screen, removing Main Thread bottleneck
      if (!isCanvasTransferred.current) {
        const canvas = vbCanvasRef.current;
        const { width, height } = canvas;

        // We must ensure no context was created before this
        // Transfer control to OffscreenCanvas
        const offscreen = canvas.transferControlToOffscreen();

        api.setConfig(useVirtualBackgroundStore.getState().config);

        // Initialize worker with transferred canvas
        await api.init(Comlink.transfer(offscreen, [offscreen]), width, height, isMobileDevice);

        // Mark as transferred so we don't try again
        isCanvasTransferred.current = true;
      }

      setIsWorkerReady(true);

      // APPLY INITIAL CONFIG
      // Ensure worker starts with correct settings immediately
      if (api.setConfig) {
        const config = useVirtualBackgroundStore.getState().config;
        await api.setConfig(config);
      }

      // BUG FIX: Use FRESH state from store, not the closure 'appliedEffect'
      // This ensures that if user changed selection while init was running, we apply the LATEST selection
      const currentAppliedEffect = useVirtualBackgroundStore.getState().appliedEffect;

      await api.setEffect(currentAppliedEffect);
      lastEffectRef.current = currentAppliedEffect;

      // NOTE: Stream will be created in processFrame after first frame is ready
      // This prevents black screen from capturing empty canvas

      isInitializingRef.current = false; // MUTEX RELEASE
      return true;
    } catch (error) {
      console.error(`[${instanceId}] Worker connection failed:`, error);
      setErrorState('worker_init_failed');
      isInitializingRef.current = false; // MUTEX RELEASE
      return false;
    }
  }, [instanceId, isMobileDevice, isWorkerReady]); // Removed appliedEffect dep to avoid recreation during init

  // Hotswap the producer track when VB initialization completes
  // This ensures that if the RoomClient started with the raw stream, we switch to VB seamlessly
  useEffect(() => {
    if (isInitialized && streamRef.current) {
      const { producers, producerLabel } = useProducersStore.getState();
      const videoProducerId = producerLabel.get('video');
      if (videoProducerId) {
        const producer = producers.get(videoProducerId);
        const track = streamRef.current.getVideoTracks()[0];
        if (producer && track && track.readyState === 'live') {
          console.log('[VB] Replacing producer track with initialized VB stream');
          const oldTrack = producer.track;
          producer
            .replaceTrack({ track })
            .then(() => {
              // Stop old track after successful replacement
              if (oldTrack && oldTrack !== track) {
                oldTrack.stop();
              }
            })
            .catch((err) => console.error('[VB] Replace track error:', err));
        }
      }
    }
  }, [isInitialized]);

  // Initialize processing pipeline
  const initProcessing = useCallback(
    async (active: boolean) => {
      if (active === isProcessingActive.current) return;

      if (active) {
        // Skip worker init if no active effect
        // This prevents unnecessary resource usage when camera is open without VB
        // Check if EITHER applied OR selected effect is active
        const { appliedEffect, selectedEffect } = useVirtualBackgroundStore.getState();
        const hasActiveEffect = appliedEffect.type !== 'none' || selectedEffect.type !== 'none';

        if (!hasActiveEffect) {
          console.log('[VB] initProcessing: Skipping - effect is strictly none. Worker will NOT start.');
          return;
        }

        try {
          const video = streamVideoRef.current;

          await initWorkerAndCanvas();

          // Wait briefly for dimensions
          let waitAttempts = 0;
          while (active && video && (video.videoWidth === 0 || video.videoHeight === 0) && waitAttempts < 10) {
            await delay(100);
            waitAttempts++;
          }
        } catch (error) {
          console.error(`[${instanceId}] Processing init error:`, error);
          setErrorState('processing_init_failed');
          handleResetStates();
        }
      } else {
        cancelAnimationFrame(animationFrameRef.current);
        isProcessingActive.current = false;
      }
    },
    [handleResetStates, initWorkerAndCanvas, instanceId]
  );

  // Handle background effect changes
  const handleEffectChange = useCallback(async () => {
    const hasChanged =
      lastEffectRef.current?.type !== appliedEffect.type || lastEffectRef.current?.value !== appliedEffect.value;

    if (workerApiRef.current && isWorkerReady && hasChanged) {
      try {
        await workerApiRef.current.setEffect(appliedEffect);
        lastEffectRef.current = appliedEffect;
      } catch (error) {
        console.error(`[${instanceId}] Error setting effect:`, error);
        setErrorState('effect_change_failed');
      }
    }
  }, [appliedEffect, isWorkerReady, instanceId]);

  // Handle error recovery
  useEffect(() => {
    if (errorState) {
      console.warn(`Recovering from error state: ${errorState}`);
      const recover = async () => {
        handleResetStates();
        if (isReadyToProcess) {
          await initProcessing(true);
        }
      };
      recover();
    }
  }, [errorState, handleResetStates, initProcessing, isReadyToProcess]);

  // Main effect handling
  useEffect(() => {
    handleEffectChange();
  }, [handleEffectChange]);

  // Initialize/cleanup based on readiness
  useEffect(() => {
    // Only init if there's an active effect (Applied or Selected)
    const { appliedEffect, selectedEffect } = useVirtualBackgroundStore.getState();
    const hasActiveEffect = appliedEffect.type !== 'none' || selectedEffect.type !== 'none';

    if (!isInitialized && hasActiveEffect) {
      initProcessing(isReadyToProcess);
    }

    if (!isReadyToProcess) {
      handleResetStates();
    }
  }, [isReadyToProcess, initProcessing, handleResetStates, isInitialized, appliedEffect.type]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      fullCleanup();
    };
  }, [fullCleanup]);

  return {
    vbCanvasRef,
    streamVideoRef,
    isInitialized,
    handleResetStates,
  };
};

export default useVirtualBackgroundHandler;
